import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Head from 'next/head';

import { config } from '@/config';
import { getEnv } from '@/lib/env';
import { buildAgentWebpageContext, setWebpageContext, trackWebpageViewed } from '@/lib/analytics/webpageAnalytics';

import Webpage from '@/components/Index/Webpage';
import WebpageASB from '@/components/Index/Webpage-ASB';
import RecruitingPage from '@/components/Index/Recruit/RecruitingPage';
import WebpageAEG from '@/components/Index/AEG/Webpage';
import WebpageArisAF360 from '@/components/Index/AF360/Webpage';
import WebpageCF360 from '@/components/Index/CF360/Webpage';
import ForbiddenWebpage from '@/components/Index/ForbiddenWebpage';
import FourOhFour from '@/components/Index/FourOhFour';
import PublicPageTemplate from '@/components/CampaignPages/PublicPage/PublicPageTemplate';
import Notification from '@/components/Base/Notification';

// Fallback OG/Twitter image used when an agent's company has not uploaded a logo.
const CF360_FALLBACK_OG_IMAGE = 'https://get.clientwave360.com/og2.png';

function stripHtml(html) {
  return (html || '')
    .toString()
    .replace(/(<([^>]+)>)/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function truncate(text, max) {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

function buildCf360Seo({ info, webpageUsername }) {
  const isCompany = !!info?.is_company_webpage;
  // Company webpages carry their fields at the payload root; agent webpages nest them.
  const corporation = (isCompany ? info : info?.corporation) || {};
  const profile = info?.profile || {};

  const corpName = corporation.name || 'ClientWave360';
  const logoUrl = corporation.logo?.url || CF360_FALLBACK_OG_IMAGE;
  const canonical = info?.webpage ? `https://${info.webpage}` : null;

  if (isCompany) {
    const description =
      truncate(stripHtml(corporation.description), 200) ||
      `${corpName} — licensed insurance help for Medicare, life, and health plans.`;
    const title = `${corpName} — Licensed Insurance Agents`;
    return {
      title,
      description,
      image: logoUrl,
      imageAlt: `${corpName} logo`,
      url: canonical,
      ogType: 'website',
      siteName: corpName,
      keywords: [
        corpName,
        'insurance agent',
        'Medicare',
        'health insurance',
        'life insurance',
        'licensed agent',
        webpageUsername
      ]
        .filter(Boolean)
        .join(', ')
    };
  }

  const firstName = profile.first_name || '';
  const lastName = profile.last_name || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim() || 'Licensed Insurance Agent';
  const agentTitle = (profile.webpage_title || profile.title || 'Licensed Insurance Agent').toString().trim();
  const locality = [profile.city, profile.state].filter(Boolean).join(', ');

  const title = corporation.name ? `${fullName} — ${agentTitle} | ${corporation.name}` : `${fullName} — ${agentTitle}`;

  const bioDescription = truncate(stripHtml(profile.about_me), 200);
  const description =
    bioDescription ||
    [
      `${fullName} is a ${agentTitle.toLowerCase()}`,
      corporation.name ? `with ${corporation.name}` : null,
      locality ? `serving ${locality}` : null
    ]
      .filter(Boolean)
      .join(' ')
      .concat(
        '. Get personalized guidance on Medicare, life, and health insurance plans from a licensed professional.'
      );

  return {
    title,
    description,
    image: logoUrl,
    imageAlt: corporation.logo?.url ? `${corpName} logo` : 'ClientWave360',
    url: canonical,
    ogType: 'profile',
    siteName: corporation.name || 'ClientWave360',
    keywords: [
      fullName,
      firstName,
      lastName,
      agentTitle,
      corporation.name,
      locality,
      'insurance agent',
      'Medicare',
      'life insurance',
      'health insurance',
      'licensed agent',
      webpageUsername
    ]
      .filter(Boolean)
      .join(', '),
    profile: { firstName, lastName }
  };
}

function isAgentSubdomainHost(host) {
  if (!host) return false;
  const clean = host.replace(/^https?:\/\//, '').split(':')[0];
  const root = clean.endsWith('.localhost') ? 'localhost' : clean.split('.').slice(-2).join('.');
  const sub = clean.slice(0, -(root.length + 1));
  return !!sub && !['api', 'platform', 'www'].includes(sub);
}

async function fetchPublicCampaignPage(slug, apiUrl, token) {
  const res = await fetch(`${apiUrl}/v1/public/campaign_pages/${slug}`, {
    headers: {
      Accept: 'application/json',
      // Server-only shared secret — added only for the SSR fetch, never in the browser.
      ...(token ? { 'X-Internal-Token': token } : {})
    }
  });
  if (!res.ok) return null;
  const json = await res.json();
  const page = json.data || json;
  return page && Object.keys(page).length ? page : null;
}

function CampaignPageView({ page, slug }) {
  const title = page.headline || page.corporation?.name;
  const description = page.supporting_copy || '';
  const image = page.video_thumbnail_url || page.corporation?.logo_url || '';
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {image && <meta property="og:image" content={image} />}
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <PublicPageTemplate page={page} slug={slug} />
      <Notification classNames="campaign-page" />
    </>
  );
}

const AgentWebpageIndex = ({ webpageUsername }) => {
  const [renderItem, setRenderItem] = useState('');
  const { publicError, info } = useSelector(state => state.public);
  const { corporation, profile } = info || {};
  const description = profile?.about_me?.replace(/(<([^>]+)>)/gi, '') || '';
  const { name, domain_name } = corporation || {};
  const image = profile?.personal_photo?.url;
  const webpage = info?.webpage;

  useEffect(() => {
    if (!webpageUsername) return;
    if (publicError) return setRenderItem(<ForbiddenWebpage />);

    if (config().ASB_SYSTEM) {
      return setRenderItem(<WebpageASB webpageUsername={webpageUsername} />);
    } else if (config().AEGLEADS) {
      return setRenderItem(<WebpageAEG webpageUsername={webpageUsername} isCompanyWebpage={info.is_company_webpage} />);
    } else if (config().AGENTFLOW360 && config(info).AF360_BU5) {
      return setRenderItem(<WebpageArisAF360 webpageUsername={webpageUsername} />);
    } else if (config(info).AF360_JOB_POSTINGS) {
      return setRenderItem(<RecruitingPage webpageUsername={webpageUsername} />);
    } else if (config().CLIENTFLOW360) {
      return setRenderItem(
        <WebpageCF360 webpageUsername={webpageUsername} isCompanyWebpage={info.is_company_webpage} />
      );
    } else {
      return setRenderItem(<Webpage webpageUsername={webpageUsername} />);
    }
  }, [publicError, webpageUsername]);

  useEffect(() => {
    if (!webpageUsername || publicError || !info) return undefined;

    setWebpageContext(buildAgentWebpageContext({ info, webpageUsername }));
    trackWebpageViewed();

    return () => setWebpageContext(null);
  }, [publicError, webpageUsername, info]);

  const isCf360 = config().CLIENTFLOW360;
  const cf360Seo =
    isCf360 && (corporation || info?.is_company_webpage) ? buildCf360Seo({ info, webpageUsername }) : null;

  return (
    <>
      <Head>
        {cf360Seo && (
          <>
            <title>{cf360Seo.title}</title>
            <meta name="robots" content="index, follow" />
            <meta name="description" content={cf360Seo.description} />
            <meta name="keywords" content={cf360Seo.keywords} />
            {cf360Seo.url && <link rel="canonical" href={cf360Seo.url} />}

            <meta property="og:type" content={cf360Seo.ogType} />
            <meta property="og:locale" content="en_US" />
            <meta property="og:site_name" content={cf360Seo.siteName} />
            {cf360Seo.url && <meta property="og:url" content={cf360Seo.url} />}
            <meta property="og:title" content={cf360Seo.title} />
            <meta property="og:description" content={cf360Seo.description} />
            <meta property="og:image" content={cf360Seo.image} />
            <meta property="og:image:alt" content={cf360Seo.imageAlt} />
            {cf360Seo.profile?.firstName && <meta property="profile:first_name" content={cf360Seo.profile.firstName} />}
            {cf360Seo.profile?.lastName && <meta property="profile:last_name" content={cf360Seo.profile.lastName} />}

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={cf360Seo.title} />
            <meta name="twitter:description" content={cf360Seo.description} />
            <meta name="twitter:image" content={cf360Seo.image} />
            <meta name="twitter:image:alt" content={cf360Seo.imageAlt} />
          </>
        )}

        {!isCf360 && corporation && (
          <>
            <meta name="robots" content="index, follow" />
            <meta name="description" content={description} />
            <meta
              name="keywords"
              content={`${webpageUsername}, ${profile.first_name}, ${profile.last_name}, ${name}, ${domain_name}, medical, insurrance, mad, market, access, direct`}
            />
            <link rel="canonical" href={getEnv('NEXT_PUBLIC_APP_URL')} />
            <meta property="og:type" content="article" />
            <meta property="og:locale" content="en_US" />
            <meta property="og:url" content={`//${webpage}`} />
            <meta property="og:title" content={getEnv('NEXT_PUBLIC_PAGE_TITLE')} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:site_name" content={getEnv('NEXT_PUBLIC_PAGE_TITLE')} />
          </>
        )}
      </Head>
      {renderItem}
    </>
  );
};

const Index = ({ webpageUsername, campaignPage, slug, isCampaignSlugRoute }) => {
  // Campaign pages are SSR-only: the gated API is fetched server-side in getInitialProps
  // (with the internal token). There is no browser fetch fallback by design.
  if (campaignPage) return <CampaignPageView page={campaignPage} slug={slug} />;

  if (webpageUsername) return <AgentWebpageIndex webpageUsername={webpageUsername} />;

  if (isCampaignSlugRoute) return <FourOhFour />;

  return <FourOhFour />;
};

Index.getInitialProps = async ({ ctx }) => {
  const slug = ctx?.query?.subdomain;
  if (!slug) return {};

  const host = ctx?.req?.headers?.host || '';
  if (isAgentSubdomainHost(host)) return {};

  const isCampaignSlugRoute = true;
  // Fetch only on the server, where the internal token is available. The campaign page is
  // SSR-only (like the agent webpage) so the browser never calls the gated API directly.
  if (ctx?.req) {
    try {
      // Mirror utils/request.js base-URL resolution. NEXT_PUBLIC_* is inlined at build time,
      // so a direct process.env.NEXT_PUBLIC_API_URL is empty on demo/prod (injected at
      // runtime); getEnv() reads it dynamically at runtime. Local dev SSR still swaps to the
      // internal API_SERVER_URL so it can reach the api container.
      let apiUrl = getEnv('NEXT_PUBLIC_API_URL') || 'http://localhost:3000';
      if (process.env.NODE_ENV === 'development' && process.env.API_SERVER_URL) {
        apiUrl = process.env.API_SERVER_URL;
      }
      const page = await fetchPublicCampaignPage(slug, apiUrl, process.env.WEBPAGE_INTERNAL_TOKEN);
      if (page) return { campaignPage: page, slug, isCampaignSlugRoute };
    } catch {}
  }

  return { slug, isCampaignSlugRoute };
};

export default Index;
